import React from 'react';
import { Link } from 'react-router-dom';
import {
    TrendingUp,
    Shield,
    CheckCircle2,
    ArrowRight,
    CreditCard,
    Hammer,
    Heart,
    Briefcase,
    GraduationCap,
    Users,
    Scale,
    Factory,
    Store,
    Smartphone,
    Zap
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import ModernCTA from '../CTA/ModernCTA';
import './ModernSector.scss';

const ModernCommercial = () => {
    const stats = [
        { number: '300+', label: 'Commercial Clients' },
        { number: '12', label: 'Industries Served' },
        { number: '99%', label: 'Client Retention' },
        { number: '15+', label: 'Years Experience' }
    ];

    const features = [
        {
            icon: <TrendingUp size={28} />,
            title: 'Measurable Results',
            description: 'Track real business outcomes and ROI from your technology investments'
        },
        {
            icon: <Shield size={28} />,
            title: 'Enhanced Security',
            description: 'Implement robust security measures to protect your digital assets'
        },
        {
            icon: <CheckCircle2 size={28} />,
            title: 'Proven Expertise',
            description: 'Leverage our years of experience across diverse industry sectors'
        }
    ];

    const industries = [
        { name: 'Banking', icon: <CreditCard size={24} /> },
        { name: 'Engineering', icon: <Hammer size={24} /> },
        { name: 'Entertainment', icon: <Heart size={24} /> },
        { name: 'Financial Services', icon: <Briefcase size={24} /> },
        { name: 'Higher Education', icon: <GraduationCap size={24} /> },
        { name: 'Hospitality', icon: <Users size={24} /> },
        { name: 'Insurance', icon: <Shield size={24} /> },
        { name: 'Legal', icon: <Scale size={24} /> },
        { name: 'Manufacturing', icon: <Factory size={24} /> },
        { name: 'Retail', icon: <Store size={24} /> },
        { name: 'Telecom', icon: <Smartphone size={24} /> },
        { name: 'Utilities', icon: <Zap size={24} /> }
    ];

    const capabilities = [
        'Enterprise-grade technology solutions',
        'Industry-specific expertise',
        'Scalable infrastructure services',
        'Dedicated account management',
        'Competitive pricing models',
        'Rapid deployment capabilities'
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Commercial"
                subtitle="Ushering in the Next Generation of Technology Solutions"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Commercial' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-sector-hero">
                <div className="jk-sector-hero__container">
                    <div className="jk-sector-hero__grid">
                        <div className="jk-sector-hero__content">
                            <span className="jk-section-label">
                                <Briefcase size={16} />
                                Commercial Solutions
                            </span>
                            <h2 className="jk-sector-hero__title">
                                Driving Measurable Outcomes for
                                <span className="highlight"> Your Business</span>
                            </h2>
                            <p className="jk-sector-hero__description">
                                At Jaak Tech, we transform your technology investments into real business
                                value. By innovating service delivery and optimizing existing IT assets,
                                we enable your teams to work faster, smarter, and more securely.
                            </p>
                            <div className="jk-sector-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Let's Connect
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    Learn More
                                </Link>
                            </div>
                        </div>

                        <div className="jk-sector-hero__stats">
                            {stats.map((stat, index) => (
                                <div key={index} className="jk-sector-hero__stat">
                                    <span className="jk-sector-hero__stat-number">{stat.number}</span>
                                    <span className="jk-sector-hero__stat-label">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="jk-sector-features">
                <div className="jk-sector-features__container">
                    <div className="jk-sector-features__header">
                        <h2 className="jk-sector-features__title">
                            Jaak Tech <span className="highlight">Advantage</span>
                        </h2>
                    </div>

                    <div className="jk-sector-features__grid">
                        {features.map((feature, index) => (
                            <div key={index} className="jk-sector-features__card">
                                <div className="jk-sector-features__card-icon">
                                    {feature.icon}
                                </div>
                                <h3 className="jk-sector-features__card-title">{feature.title}</h3>
                                <p className="jk-sector-features__card-text">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Industries Section */}
            <section className="jk-sector-industries">
                <div className="jk-sector-industries__container">
                    <div className="jk-sector-industries__header">
                        <span className="jk-section-label">
                            <Briefcase size={16} />
                            Industries
                        </span>
                        <h2 className="jk-sector-industries__title">
                            Industries We <span className="highlight">Serve</span>
                        </h2>
                        <p className="jk-sector-industries__subtitle">
                            We serve a diverse range of commercial industries, delivering tailored
                            technology solutions that drive success across sectors.
                        </p>
                    </div>

                    <div className="jk-sector-industries__grid">
                        {industries.map((industry, index) => (
                            <div key={index} className="jk-sector-industries__card">
                                <div className="jk-sector-industries__card-icon">
                                    {industry.icon}
                                </div>
                                <span className="jk-sector-industries__card-name">{industry.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-sector-capabilities">
                <div className="jk-sector-capabilities__container">
                    <div className="jk-sector-capabilities__grid">
                        <div className="jk-sector-capabilities__content">
                            <span className="jk-section-label">
                                <TrendingUp size={16} />
                                Our Capabilities
                            </span>
                            <h2 className="jk-sector-capabilities__title">
                                Transform Your
                                <span className="highlight"> Technology Investments</span>
                            </h2>
                            <p className="jk-sector-capabilities__text">
                                Ready to unlock the full potential of your technology stack? We help
                                commercial organizations drive measurable outcomes through innovative
                                technology solutions and strategic partnerships.
                            </p>

                            <ul className="jk-sector-capabilities__list">
                                {capabilities.map((item, index) => (
                                    <li key={index} className="jk-sector-capabilities__item">
                                        <CheckCircle2 size={20} className="jk-sector-capabilities__item-icon" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <Link to="/contact" className="jk-btn jk-btn--primary">
                                Schedule Consultation
                                <ArrowRight size={20} />
                            </Link>
                        </div>

                        <div className="jk-sector-capabilities__visual">
                            <div className="jk-sector-compliance">
                                <h3 className="jk-sector-compliance__title">Enterprise Solutions</h3>
                                <p className="jk-sector-compliance__text">
                                    Complete technology solutions for businesses of all sizes
                                </p>
                                <ul className="jk-sector-compliance__list">
                                    <li><CheckCircle2 size={16} /> Hardware & Software</li>
                                    <li><CheckCircle2 size={16} /> Cloud Infrastructure</li>
                                    <li><CheckCircle2 size={16} /> Managed Services</li>
                                    <li><CheckCircle2 size={16} /> Cybersecurity</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernCommercial;
